import * as S from './styles';
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useSelector, shallowEqual } from 'react-redux';
import { RootReducer } from 'redux/reducers';
import { Frame, CommentFrameEntry } from 'dragonmouse-shared/interfaces';
import MultilineInput from 'components/Widget/Input/MultilineInput';
import forge from 'apis/forge';
import { v4 as uuid } from 'uuid';
import { trackOutputAction } from 'analytics';
import { canEditFrame } from 'redux/db/util';
import { displayViewOnlyModal } from 'redux/mode/util';
import { AnchorPoint, measureTextIndex } from 'hooks/useAnchored';
import MentionPicker from '../MentionPicker';
import MentionCard from '../MentionCard';
import {
	MentionUser,
	computeMentionRanges,
	filterMentionUsers,
	findMentionSession,
	useMentionUsers,
} from '../mentions';

interface CommentInputProps {
	frame: Frame;
	elementId?: string;
	readonly?: boolean;
	parentId?: string;
	// Author of the comment being replied to; the draft opens with them
	// pre-mentioned so notifying them is the default and deleting the text
	// opts out.
	replyToUserId?: string;
	autoFocus?: boolean;
	onCancel?: () => void;
	onSubmit?: () => void;
}

const CommentInput = ({
	frame,
	elementId,
	readonly = false,
	parentId,
	replyToUserId,
	autoFocus,
	onCancel,
	onSubmit: onSubmitProp,
}: CommentInputProps) => {
	const mentionUsers = useMentionUsers();

	// No prefill for self-replies (self-mentions never email) or authors who
	// are no longer scene users (unmentionable). Read once at mount.
	const prefillAuthor = replyToUserId
		? (mentionUsers.find((u) => u.id === replyToUserId && !u.isSelf) ?? null)
		: null;
	const prefillText = prefillAuthor ? `@${prefillAuthor.displayName} ` : '';

	const [comment, setComment] = useState<string>(prefillText);
	const [isFocused, setIsFocused] = useState<boolean>(false);

	// Mentions are plain text: `mentioned` remembers who was picked, and a
	// mention is live only while '@' + their exact display name survives in the
	// text. Any edit that breaks the match silently demotes it to plain text.
	const [mentioned, setMentioned] = useState<MentionUser[]>(
		prefillAuthor ? [prefillAuthor] : []
	);
	const [caret, setCaret] = useState<number | null>(null);
	const [dismissedAnchor, setDismissedAnchor] = useState<number | null>(null);
	const [selectedIndex, setSelectedIndex] = useState(0);
	const [pickerAnchor, setPickerAnchor] = useState<AnchorPoint | null>(null);
	const [mentionAnchor, setMentionAnchor] = useState<AnchorPoint | null>(null);
	const [, setRemeasureTick] = useState(0);

	const textareaRef = useRef<HTMLTextAreaElement>(null);
	const sizerRef = useRef<HTMLDivElement>(null);
	// Pre-seeded so the mount pass places the caret after the prefilled mention.
	const pendingCaretRef = useRef<number | null>(prefillText ? prefillText.length : null);

	const { authUser } = useSelector(
		(r: RootReducer) => ({
			authUser: r.mode.authUser,
		}),
		shallowEqual
	);

	//----------------------------------------------------------------------
	// HOOKS
	//----------------------------------------------------------------------

	const ranges = useMemo(() => computeMentionRanges(comment, mentioned), [comment, mentioned]);

	const session = useMemo(
		() => findMentionSession(comment, caret, ranges),
		[comment, caret, ranges]
	);

	const matches = useMemo(
		() => (session ? filterMentionUsers(mentionUsers, session.query) : []),
		[mentionUsers, session?.query, session !== null]
	);

	const isPickerOpen =
		isFocused && !!session && session.anchor !== dismissedAnchor && matches.length > 0;
	const activeIndex = Math.min(selectedIndex, Math.max(0, matches.length - 1));

	// The caret sitting strictly inside a mention summons the identity card.
	const caretMentionIndex = useMemo(() => {
		if (caret === null || isPickerOpen || !isFocused) return -1;
		return ranges.findIndex((r) => caret > r.start && caret < r.end);
	}, [ranges, caret, isPickerOpen, isFocused]);

	// Reset keyboard selection whenever the filter changes.
	useEffect(() => {
		setSelectedIndex(0);
	}, [session?.anchor, session?.query]);

	// Restore the caret after a programmatic text replacement (mention insert).
	useLayoutEffect(() => {
		const pending = pendingCaretRef.current;
		if (pending === null) return;
		pendingCaretRef.current = null;
		textareaRef.current?.setSelectionRange(pending, pending);
		setCaret(pending);
	}, [comment]);

	// Anchors are in viewport coordinates, measured fresh after every render
	// (guarded state writes keep this from looping). Scroll/resize just forces
	// another pass.
	useLayoutEffect(() => {
		const samePoint = (a: AnchorPoint | null, b: AnchorPoint | null) =>
			a === b || (!!a && !!b && Math.abs(a.left - b.left) < 0.5 && Math.abs(a.top - b.top) < 0.5);

		// Anchored at the '@' rather than the moving caret so the menu holds
		// still while the filter is typed.
		const nextPicker =
			isPickerOpen && session ? measureTextIndex(sizerRef.current, session.anchor) : null;
		setPickerAnchor((prev) => (samePoint(prev, nextPicker) ? prev : nextPicker));

		let nextMention: AnchorPoint | null = null;
		if (caretMentionIndex >= 0) {
			const el = sizerRef.current?.querySelector(`[data-mention-index="${caretMentionIndex}"]`);
			const rect = el?.getBoundingClientRect();
			nextMention = rect ? { left: rect.left, top: rect.top, bottom: rect.bottom } : null;
		}
		setMentionAnchor((prev) => (samePoint(prev, nextMention) ? prev : nextMention));
	});

	useEffect(() => {
		if (!isPickerOpen && caretMentionIndex < 0) return;
		const bump = () => setRemeasureTick((n) => n + 1);
		window.addEventListener('scroll', bump, true);
		window.addEventListener('resize', bump);
		return () => {
			window.removeEventListener('scroll', bump, true);
			window.removeEventListener('resize', bump);
		};
	}, [isPickerOpen, caretMentionIndex >= 0]);

	//----------------------------------------------------------------------
	// HANDLERS
	//----------------------------------------------------------------------

	const onPickMention = (mentionUser: MentionUser) => {
		if (!session || caret === null) return;
		trackOutputAction('mention_added');
		const insert = `@${mentionUser.displayName} `;
		const next = comment.slice(0, session.anchor) + insert + comment.slice(caret);
		pendingCaretRef.current = session.anchor + insert.length;
		setComment(next);
		setMentioned((prev) =>
			prev.some((u) => u.id === mentionUser.id) ? prev : [...prev, mentionUser]
		);
		setDismissedAnchor(null);
	};

	// forge.addFrameEntry is an optimistic write — the new comment appears in the
	// UI synchronously via Redux, so there's no loading state to manage here.
	const onSubmit = () => {
		if (!comment.trim() || !authUser || readonly) return;

		if (!canEditFrame(frame)) {
			displayViewOnlyModal();
			return;
		}

		trackOutputAction('add_comment');
		// Only picked mentions whose '@Display Name' text survived editing are
		// recorded; the server re-validates the ids before emailing anyone.
		const message = comment.trim();
		const surviving = computeMentionRanges(message, mentioned);
		const mentionedUserIds = Array.from(new Set(surviving.map((r) => r.mentionUser.id)));

		const newComment: CommentFrameEntry = {
			id: uuid(),
			kind: 'comment',
			message,
			userId: authUser.id,
			createdAt: new Date(),
			deletedAt: null,
			elementId: elementId ?? null,
			siteId: frame.siteId ?? undefined,
			resolvedAt: null,
			parentId: parentId ?? null,
			mentionedUserIds: mentionedUserIds.length > 0 ? mentionedUserIds : undefined,
		};

		forge.addFrameEntry({
			frameId: frame.id,
			entry: newComment,
		});
		setComment('');
		setMentioned([]);
		setDismissedAnchor(null);
		onSubmitProp?.();
	};

	const onChangeComment = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
		setComment(e.target.value);
		setCaret(e.target.selectionStart);
	};

	const onSelectionChange = (e: React.SyntheticEvent<HTMLTextAreaElement>) => {
		setCaret(e.currentTarget.selectionStart);
	};

	const onKeyDown = (e: React.KeyboardEvent<HTMLElement>) => {
		// The picker owns navigation keys while it's open; everything else
		// falls through so typing stays completely ordinary.
		if (isPickerOpen) {
			if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
				e.preventDefault();
				const delta = e.key === 'ArrowDown' ? 1 : -1;
				setSelectedIndex((activeIndex + delta + matches.length) % matches.length);
				return;
			}
			if (e.key === 'Enter' || e.key === 'Tab') {
				e.preventDefault();
				onPickMention(matches[activeIndex]);
				return;
			}
			if (e.key === 'Escape') {
				e.preventDefault();
				setDismissedAnchor(session!.anchor);
				return;
			}
		}

		// Skip Enter while an IME is composing (e.g. Japanese/Chinese/Korean input)
		// and skip key-repeat to avoid duplicate submits from a held key.
		const isComposing = (e.nativeEvent as KeyboardEvent).isComposing;
		if (e.key === 'Enter' && !e.shiftKey && !isComposing && !e.repeat) {
			e.preventDefault();
			onSubmit();
		} else if (e.key === 'Escape' && onCancel) {
			e.preventDefault();
			onCancel();
		}
	};

	//----------------------------------------------------------------------
	// RENDERING
	//----------------------------------------------------------------------

	// The mirror repaints the text with mention spans. Only color/background
	// may differ from the plain text or the caret desynchronizes.
	const mirrorContent = useMemo(() => {
		if (ranges.length === 0) return undefined;
		const nodes: React.ReactNode[] = [];
		let pos = 0;
		ranges.forEach((range, i) => {
			if (range.start > pos) nodes.push(comment.slice(pos, range.start));
			nodes.push(
				<S.MentionMark key={i} data-mention-index={i} $self={range.mentionUser.isSelf}>
					{comment.slice(range.start, range.end)}
				</S.MentionMark>
			);
			pos = range.end;
		});
		if (pos < comment.length) nodes.push(comment.slice(pos));
		return nodes;
	}, [comment, ranges]);

	// No inline submit button — Enter submits, Escape cancels (in reply mode).
	// Keeps the input feeling like a regular text field rather than a chat input.
	return (
		<S.Base>
			{readonly ? (
				<S.ReadOnlyText>
					You're in view-only mode. To add comments, ask the owner for editing permissions.
				</S.ReadOnlyText>
			) : (
				<>
					<S.InputArea isFocused={isFocused}>
						<S.MultilineInputWrapper>
							<MultilineInput
								ref={textareaRef}
								value={comment}
								onInput={onChangeComment}
								onSelect={onSelectionChange}
								onKeyDown={onKeyDown}
								placeholder={parentId ? 'Reply...' : 'Add a comment...'}
								minLines={2}
								renderBorder={false}
								autoFocus={autoFocus}
								mirrorContent={mirrorContent}
								mirrorRef={sizerRef}
								onFocusChange={(isFocused) => {
									setIsFocused(isFocused);
								}}
							/>
						</S.MultilineInputWrapper>
					</S.InputArea>
					{isPickerOpen && (
						<MentionPicker
							mentionUsers={matches}
							selectedIndex={activeIndex}
							anchor={pickerAnchor}
							onSelect={onPickMention}
							onHoverIndex={setSelectedIndex}
						/>
					)}
					{caretMentionIndex >= 0 && ranges[caretMentionIndex] && (
						<MentionCard
							mentionUser={ranges[caretMentionIndex].mentionUser}
							anchor={mentionAnchor}
						/>
					)}
				</>
			)}
		</S.Base>
	);
};

export default CommentInput;
